import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button, Heading, InlineAlert, Modal } from '@design-systems/canvas-ui'
import { getApiErrorMessage } from '../../../shared/api/apiClient'
import { useBrand } from '../../../shared/brand/context/BrandContext'
import { mapPromptModeVersionResponse } from '../../../shared/brand/mappers/promptModeMapper'
import SideBySideTextDiff from '../../../shared/components/SideBySideTextDiff/SideBySideTextDiff'

const versionColumns = [
  {
    id: 'version',
    label: 'Version',
    width: 100,
  },
  {
    id: 'changeSummary',
    label: 'Summary',
    width: 280,
  },
  {
    id: 'publishedBy',
    label: 'Published By',
    width: 280,
  },
  {
    id: 'published',
    label: 'Published',
    width: 220,
  },
  {
    id: 'status',
    label: 'Status',
    width: 140,
  },
  {
    align: 'center',
    id: 'actions',
    label: 'Actions',
    width: 320,
  },
]

const getColumnClassName = (column, type = 'cell') => {
  const classNames = [
    type === 'header'
      ? 'configuration-list__header-content'
      : 'configuration-list__cell-content',
  ]

  if (column.align === 'center') {
    classNames.push(
      type === 'header'
        ? 'configuration-list__header-content--center'
        : 'configuration-list__cell-content--center',
    )
  }

  if (column.id === 'actions') {
    classNames.push(
      type === 'header'
        ? 'configuration-list__header-content--actions'
        : 'configuration-list__cell-content--actions',
    )
  }

  return classNames.join(' ')
}

const stopModalEventPropagation = (event) => {
  event.stopPropagation()
}

const getDraftSeedText = (version) => version?.instructionText || ''

const getComparePanelId = (versionId) => {
  return `skill-instructions-compare-${versionId}`
}

const getCellDisplayValue = (value) => {
  const formattedValue = `${value ?? ''}`.trim()

  return formattedValue.length > 0 ? formattedValue : '-'
}

const getDefaultSelectedVersion = (versions) => {
  return versions.find((version) => version.active) || versions[0] || null
}

const SkillInstructionsModal = ({ isOpen, mode, onClose }) => {
  const {
    getSkillInstructionVersions,
    publishSkillInstructionVersion,
    rollbackSkillInstructionVersion,
    saveSkillInstructionDraftVersion,
  } = useBrand()
  const skillCode = mode?.code || ''
  const [draftChangeSummary, setDraftChangeSummary] = useState('')
  const [instructionText, setInstructionText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [isPublishing, setIsPublishing] = useState(false)
  const [rollingBackVersionId, setRollingBackVersionId] = useState('')
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [isSaveDraftModalOpen, setIsSaveDraftModalOpen] = useState(false)
  const [publishError, setPublishError] = useState('')
  const [rollbackError, setRollbackError] = useState('')
  const [saveDraftError, setSaveDraftError] = useState('')
  const [selectedVersionId, setSelectedVersionId] = useState('')
  const [versionRows, setVersionRows] = useState([])
  const [draftBaselineText, setDraftBaselineText] = useState('')
  const [expandedCompareVersionId, setExpandedCompareVersionId] = useState('')
  const [isCloseWarningOpen, setIsCloseWarningOpen] = useState(false)
  const instructionTextareaRef = useRef(null)

  const applyLoadedVersions = useCallback((versions) => {
    const defaultSelectedVersion = getDefaultSelectedVersion(versions)
    const draftVersion = versions.find((version) => version.status === 'draft')

    setVersionRows(versions)
    setSelectedVersionId(defaultSelectedVersion?.id || '')
    setInstructionText(defaultSelectedVersion?.instructionText || '')
    setDraftBaselineText(getDraftSeedText(draftVersion))
    setExpandedCompareVersionId('')
  }, [])

  const resetModalState = useCallback(() => {
    setDraftChangeSummary('')
    setInstructionText('')
    setIsLoading(false)
    setLoadError('')
    setIsSaveDraftModalOpen(false)
    setPublishError('')
    setRollbackError('')
    setRollingBackVersionId('')
    setSaveDraftError('')
    setSelectedVersionId('')
    setVersionRows([])
    setDraftBaselineText('')
    setExpandedCompareVersionId('')
    setIsCloseWarningOpen(false)
  }, [])

  const resetSaveDraftState = useCallback(() => {
    setDraftChangeSummary('')
    setSaveDraftError('')
  }, [])

  const loadVersions = useCallback(
    async ({ mountedOnly = true } = {}) => {
      const versions = await getSkillInstructionVersions(skillCode)
      const normalizedVersions = mapPromptModeVersionResponse(versions)

      if (!mountedOnly) {
        applyLoadedVersions(normalizedVersions)
      }

      return normalizedVersions
    },
    [applyLoadedVersions, getSkillInstructionVersions, skillCode],
  )

  useEffect(() => {
    if (!isOpen || !skillCode) {
      resetModalState()
      return
    }

    let isMounted = true
    resetModalState()
    setIsLoading(true)

    loadVersions()
      .then((normalizedVersions) => {
        if (!isMounted) {
          return
        }

        applyLoadedVersions(normalizedVersions)
      })
      .catch((error) => {
        if (!isMounted) {
          return
        }

        setLoadError(
          getApiErrorMessage(
            error,
            'We could not load the skill instructions. Please try again.',
          ),
        )
        setInstructionText('')
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [
    applyLoadedVersions,
    isOpen,
    loadVersions,
    mode,
    skillCode,
    resetModalState,
  ])

  const currentDraftVersion = useMemo(
    () => versionRows.find((version) => version.status === 'draft'),
    [versionRows],
  )
  const isDraftSelected = selectedVersionId === currentDraftVersion?.id
  const hasDraft = Boolean(currentDraftVersion)
  const hasDraftTextChanges =
    getDraftSeedText(currentDraftVersion) !== draftBaselineText
  const isDraftEditable = hasDraft && isDraftSelected

  useEffect(() => {
    if (!isDraftEditable) {
      return
    }

    instructionTextareaRef.current?.focus()
  }, [isDraftEditable, selectedVersionId])

  const closeSaveDraftModal = () => {
    setIsSaveDraftModalOpen(false)
    resetSaveDraftState()
  }

  const handleOpenSaveDraftModal = () => {
    if (!isDraftEditable) {
      return
    }

    resetSaveDraftState()
    setIsSaveDraftModalOpen(true)
  }

  const handleCreateDraft = () => {
    const seedText = getDraftSeedText(selectedVersion)

    setVersionRows((currentRows) => [
      {
        active: false,
        changeSummary: '',
        id: 'draft-local',
        instructionText: seedText,
        promptVersionId: '',
        published: '',
        publishedAt: '',
        status: 'draft',
        version: 'Draft',
      },
      ...currentRows,
    ])
    setDraftBaselineText(seedText)
    setExpandedCompareVersionId('')
    setSelectedVersionId('draft-local')
    setInstructionText(seedText)
  }

  const finalizeClose = useCallback(() => {
    setIsCloseWarningOpen(false)
    onClose()
  }, [onClose])

  const requestClose = useCallback(() => {
    if (hasDraftTextChanges) {
      setIsCloseWarningOpen(true)
      return
    }

    finalizeClose()
  }, [finalizeClose, hasDraftTextChanges])

  const handleDiscardClose = useCallback(() => {
    const baselineText = draftBaselineText

    if (currentDraftVersion?.id) {
      setVersionRows((currentRows) =>
        currentRows.map((version) =>
          version.id === currentDraftVersion.id
            ? { ...version, instructionText: baselineText }
            : version,
        ),
      )
    }

    if (isDraftSelected) {
      setInstructionText(baselineText)
    }

    finalizeClose()
  }, [
    currentDraftVersion?.id,
    draftBaselineText,
    finalizeClose,
    isDraftSelected,
  ])

  const handleSaveDraft = async ({ closeAfterSave = false } = {}) => {
    if (!skillCode) {
      return
    }

    setIsSavingDraft(true)
    setSaveDraftError('')

    try {
      await saveSkillInstructionDraftVersion(
        skillCode,
        {
          changeSummary: draftChangeSummary,
          instructionText: getDraftSeedText(currentDraftVersion),
        },
        currentDraftVersion?.promptVersionId || undefined,
      )
      closeSaveDraftModal()
      await loadVersions({ mountedOnly: false })
      if (closeAfterSave) {
        finalizeClose()
      }
    } catch (error) {
      setSaveDraftError(
        getApiErrorMessage(
          error,
          'We could not save the skill instruction draft. Please try again.',
        ),
      )
    } finally {
      setIsSavingDraft(false)
    }
  }

  const handlePublish = useCallback(async () => {
    if (!currentDraftVersion?.promptVersionId) {
      return
    }

    setIsPublishing(true)
    setPublishError('')

    try {
      await publishSkillInstructionVersion(currentDraftVersion.promptVersionId)
      await loadVersions({ mountedOnly: false })
    } catch (error) {
      setPublishError(
        getApiErrorMessage(
          error,
          'We could not publish the skill instruction version. Please try again.',
        ),
      )
    } finally {
      setIsPublishing(false)
    }
  }, [
    currentDraftVersion?.promptVersionId,
    loadVersions,
    publishSkillInstructionVersion,
  ])

  const handleRollback = useCallback(
    async (promptVersionId) => {
      if (!promptVersionId) {
        return
      }

      setRollingBackVersionId(promptVersionId)
      setRollbackError('')

      try {
        await rollbackSkillInstructionVersion(promptVersionId)
        await loadVersions({ mountedOnly: false })
      } catch (error) {
        setRollbackError(
          getApiErrorMessage(
            error,
            'We could not rollback the skill instruction version. Please try again.',
          ),
        )
      } finally {
        setRollingBackVersionId('')
      }
    },
    [loadVersions, rollbackSkillInstructionVersion],
  )

  const activeVersion = versionRows.find((version) => version.active)
  const publishedVersionCount = versionRows.filter(
    (version) => version.status !== 'draft',
  ).length
  const selectedVersion =
    versionRows.find((version) => version.id === selectedVersionId) ||
    activeVersion ||
    versionRows[0] ||
    null
  const compareVersion =
    versionRows.find((version) => version.id === expandedCompareVersionId) ||
    null
  const versionSummary = `${
    activeVersion?.version
      ? `${activeVersion.version} (active)`
      : 'No active version'
  } • ${publishedVersionCount} version${publishedVersionCount === 1 ? '' : 's'}`
  const compareDetails = useMemo(() => {
    if (!activeVersion || !compareVersion) {
      return null
    }

    return {
      baseLabel: activeVersion.version
        ? `${activeVersion.version} (active)`
        : 'Active version',
      baseText: activeVersion.instructionText,
      compareLabel: compareVersion.version || 'Selected version',
      compareVersionId: compareVersion.id,
      compareText: compareVersion.instructionText,
    }
  }, [activeVersion, compareVersion])

  const selectVersion = useCallback((version) => {
    setSelectedVersionId(version.id)
    setInstructionText(version.instructionText || '')
  }, [])

  const handleToggleCompare = useCallback(
    (event, version) => {
      event.stopPropagation()

      if (!activeVersion) {
        return
      }

      setExpandedCompareVersionId((currentVersionId) =>
        currentVersionId === version.id ? '' : version.id,
      )
    },
    [activeVersion],
  )

  const handleInstructionTextChange = useCallback(
    (event) => {
      const nextValue = event.target.value

      setInstructionText(nextValue)

      if (!isDraftSelected) {
        return
      }

      setVersionRows((currentRows) =>
        currentRows.map((version) =>
          version.id === currentDraftVersion?.id
            ? { ...version, instructionText: nextValue }
            : version,
        ),
      )
    },
    [currentDraftVersion?.id, isDraftSelected],
  )

  const getVersionCellInteractionProps = useCallback(
    (columnId, version) => {
      if (columnId === 'actions') {
        return {}
      }

      const onSelect = () => selectVersion(version)

      if (columnId === 'version') {
        return {
          'aria-pressed': selectedVersionId === version.id,
          className: 'skill-instructions-modal__row-trigger',
          onClick: onSelect,
          onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onSelect()
            }
          },
          role: 'button',
          tabIndex: 0,
        }
      }

      return {
        className: 'skill-instructions-modal__row-target',
        onClick: onSelect,
      }
    },
    [selectVersion, selectedVersionId],
  )

  const renderVersionCell = useCallback(
    (column, version) => {
      const interactionProps = getVersionCellInteractionProps(
        column.id,
        version,
      )
      const isCompareExpanded = expandedCompareVersionId === version.id
      const comparePanelId = getComparePanelId(version.id)
      const canCompare = Boolean(activeVersion) && !version.active

      if (column.id === 'actions') {
        const compareButtonLabel = isCompareExpanded ? 'Hide' : 'Compare'

        return (
          <div className={getColumnClassName(column)}>
            {version.status === 'draft' ? (
              <div className="skill-instructions-modal__actions">
                {canCompare ? (
                  <Button
                    aria-controls={comparePanelId}
                    aria-expanded={isCompareExpanded}
                    className="skill-instructions-modal__action-button"
                    isDisabled={!canCompare}
                    onClick={(event) => handleToggleCompare(event, version)}
                    size="sm"
                    type="button"
                    variant="secondary"
                  >
                    {compareButtonLabel}
                  </Button>
                ) : null}
                <Button
                  className="skill-instructions-modal__action-button"
                  isLoading={isPublishing}
                  onClick={handlePublish}
                  isDisabled={!currentDraftVersion?.promptVersionId}
                  size="sm"
                  type="button"
                  variant="primary"
                >
                  Publish
                </Button>
              </div>
            ) : !version.active ? (
              <div className="skill-instructions-modal__actions">
                {canCompare ? (
                  <Button
                    aria-controls={comparePanelId}
                    aria-expanded={isCompareExpanded}
                    className="skill-instructions-modal__action-button"
                    isDisabled={!canCompare}
                    onClick={(event) => handleToggleCompare(event, version)}
                    size="sm"
                    type="button"
                    variant="secondary"
                  >
                    {compareButtonLabel}
                  </Button>
                ) : null}
                <Button
                  className="skill-instructions-modal__action-button"
                  isLoading={rollingBackVersionId === version.promptVersionId}
                  onClick={() => handleRollback(version.promptVersionId)}
                  size="sm"
                  type="button"
                  variant="secondary"
                >
                  Rollback
                </Button>
              </div>
            ) : null}
          </div>
        )
      }

      if (column.id === 'status') {
        return (
          <div
            {...interactionProps}
            className={`${getColumnClassName(column)} ${
              interactionProps.className || ''
            }`.trim()}
          >
            <span
              className={`configuration-list__status ${
                version.active ? '' : 'configuration-list__status--inactive'
              }`}
            >
              {getCellDisplayValue(version.status)}
            </span>
          </div>
        )
      }

      return (
        <div
          {...interactionProps}
          className={`${getColumnClassName(column)} ${
            interactionProps.className || ''
          }`.trim()}
        >
          {getCellDisplayValue(version[column.id])}
        </div>
      )
    },
    [
      activeVersion,
      currentDraftVersion?.promptVersionId,
      expandedCompareVersionId,
      getVersionCellInteractionProps,
      handlePublish,
      handleRollback,
      handleToggleCompare,
      isPublishing,
      rollingBackVersionId,
    ],
  )

  const versionTable = useMemo(
    () => (
      <table
        aria-label="Instruction version history"
        className="configuration-list__table skill-instructions-modal__version-table"
      >
        <colgroup>
          {versionColumns.map((column) => (
            <col key={column.id} style={{ width: column.width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {versionColumns.map((column) => (
              <th key={column.id} scope="col">
                <span className={getColumnClassName(column, 'header')}>
                  {column.label}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {versionRows.map((version) => {
            const isCompareExpanded = expandedCompareVersionId === version.id

            return (
              <Fragment key={version.id}>
                <tr
                  className={`skill-instructions-modal__version-row ${
                    isCompareExpanded
                      ? 'skill-instructions-modal__version-row--expanded'
                      : ''
                  }`.trim()}
                >
                  {versionColumns.map((column) => (
                    <td key={column.id}>
                      {renderVersionCell(column, version)}
                    </td>
                  ))}
                </tr>
                {isCompareExpanded &&
                compareDetails?.compareVersionId === version.id ? (
                  <tr className="skill-instructions-modal__compare-row">
                    <td colSpan={versionColumns.length}>
                      <SideBySideTextDiff
                        baseLabel={compareDetails.baseLabel}
                        baseText={compareDetails.baseText}
                        compareLabel={compareDetails.compareLabel}
                        compareText={compareDetails.compareText}
                        panelId={getComparePanelId(version.id)}
                        regionLabel="Version comparison"
                      />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    ),
    [compareDetails, expandedCompareVersionId, renderVersionCell, versionRows],
  )

  const footer = (
    <div
      className="skill-instructions-modal__footer"
      onMouseDown={stopModalEventPropagation}
      onPointerDown={stopModalEventPropagation}
    >
      <Button onClick={requestClose} variant="secondary">
        Close
      </Button>
    </div>
  )

  const saveDraftFooter = (
    <div
      className="skill-instructions-modal__save-draft-footer"
      onMouseDown={stopModalEventPropagation}
      onPointerDown={stopModalEventPropagation}
    >
      <Button onClick={closeSaveDraftModal} variant="secondary">
        Cancel
      </Button>
      <Button
        isDisabled={!hasDraftTextChanges}
        isLoading={isSavingDraft}
        onClick={handleSaveDraft}
        type="button"
        variant="primary"
      >
        Save Draft
      </Button>
    </div>
  )

  return (
    <>
      <Modal
        footer={footer}
        headingText="Update skill instruction"
        isOpen={isOpen}
        onClose={requestClose}
        scrollBehavior="contained"
        size="lg"
      >
        <div
          className="skill-instructions-modal"
          onMouseDown={stopModalEventPropagation}
          onPointerDown={stopModalEventPropagation}
        >
          <header className="skill-instructions-modal__header">
            <div>
              <Heading as="h2" size="4">
                {mode?.name || 'Skill'}
              </Heading>
              <p className="skill-instructions-modal__summary">
                {isLoading ? 'Loading instruction versions…' : versionSummary}
              </p>
            </div>
            <div className="skill-instructions-modal__header-actions">
              {!hasDraft ? (
                <Button
                  onClick={handleCreateDraft}
                  type="button"
                  variant="secondary"
                >
                  Create Draft
                </Button>
              ) : isDraftEditable ? (
                <Button
                  isDisabled={!hasDraftTextChanges}
                  onClick={handleOpenSaveDraftModal}
                  type="button"
                  variant="secondary"
                >
                  Save Draft
                </Button>
              ) : null}
            </div>
          </header>

          {loadError && (
            <InlineAlert heading="Unable to load instructions" variant="error">
              {loadError}
            </InlineAlert>
          )}

          {publishError && (
            <InlineAlert heading="Unable to publish version" variant="error">
              {publishError}
            </InlineAlert>
          )}

          {rollbackError && (
            <InlineAlert heading="Unable to rollback version" variant="error">
              {rollbackError}
            </InlineAlert>
          )}

          <section className="skill-instructions-modal__section">
            <Heading as="h3" size="sm">
              {selectedVersion?.version
                ? `${selectedVersion.version} Instruction`
                : 'Instruction'}
            </Heading>
            <textarea
              ref={instructionTextareaRef}
              className="skill-instructions-modal__textarea"
              readOnly={!isDraftEditable}
              onChange={handleInstructionTextChange}
              placeholder="No instruction available yet."
              rows={12}
              value={instructionText}
            />
          </section>

          <section className="skill-instructions-modal__section">
            <Heading as="h3" size="sm">
              Version History
            </Heading>
            {isLoading && versionRows.length === 0 ? (
              <p role="status">Loading instruction versions…</p>
            ) : versionRows.length > 0 ? (
              <div
                aria-label="Instruction version history"
                className="configuration-list"
                role="region"
              >
                {versionTable}
              </div>
            ) : (
              <p className="configuration-list__empty">
                No instruction versions have been published yet.
              </p>
            )}
          </section>
        </div>
      </Modal>

      <Modal
        footer={saveDraftFooter}
        headingText="Save draft"
        isOpen={isSaveDraftModalOpen}
        onClose={closeSaveDraftModal}
        scrollBehavior="contained"
        size="sm"
      >
        <div
          className="skill-instructions-modal__save-draft"
          onMouseDown={stopModalEventPropagation}
          onPointerDown={stopModalEventPropagation}
        >
          {saveDraftError && (
            <InlineAlert heading="Unable to save draft" variant="error">
              {saveDraftError}
            </InlineAlert>
          )}
          <label
            className="skill-instructions-modal__field"
            htmlFor="skill-instructions-draft-change-summary"
          >
            <span className="skill-instructions-modal__field-label">
              Change Summary
            </span>
            <input
              className="skill-instructions-modal__input"
              id="skill-instructions-draft-change-summary"
              onChange={(event) => setDraftChangeSummary(event.target.value)}
              placeholder="Optional summary of the draft changes"
              type="text"
              value={draftChangeSummary}
            />
          </label>
        </div>
      </Modal>

      <Modal
        footer={
          <div className="skill-instructions-modal__save-draft-footer">
            <Button onClick={handleDiscardClose} variant="secondary">
              Discard
            </Button>
            <Button
              isLoading={isSavingDraft}
              onClick={() => handleSaveDraft({ closeAfterSave: true })}
              type="button"
              variant="primary"
            >
              Save Draft
            </Button>
          </div>
        }
        headingText="Unsaved draft changes"
        isOpen={isCloseWarningOpen}
        onClose={() => {
          setIsCloseWarningOpen(false)
        }}
        scrollBehavior="contained"
        size="sm"
      >
        <p>Save your draft changes before closing this popup.</p>
      </Modal>
    </>
  )
}

export default SkillInstructionsModal
